import React, { memo, useCallback } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DEPENDS_ON_PILL_PREFIX, getDependsOnPillAriaLabel } from './DependsOnPills.constants';
import {
  dependsOnPillReadOnlyStyles,
  dependsOnPillStyles,
  dependsOnRowStyles,
} from './DependsOnPills.styles';

const DependsOnPill = ({ stepKey, onSelect }) => {
  const isClickable = _.isFunction(onSelect);
  const onClick = useCallback(
    e => {
      if (!isClickable) {
        return;
      }

      e.stopPropagation();
      onSelect(stepKey);
    },
    [isClickable, onSelect, stepKey],
  );

  const onKeyDown = useCallback(
    e => {
      if (!isClickable) {
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onSelect(stepKey);
      }
    },
    [isClickable, onSelect, stepKey],
  );

  if (!isClickable) {
    return (
      <Box sx={dependsOnPillReadOnlyStyles}>
        <Typography color="text.secondary" variant="regular12">
          {DEPENDS_ON_PILL_PREFIX} {stepKey}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      aria-label={getDependsOnPillAriaLabel(stepKey)}
      role="button"
      sx={dependsOnPillStyles}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <Typography color="text.secondary" variant="regular12">
        {DEPENDS_ON_PILL_PREFIX} {stepKey}
      </Typography>
    </Box>
  );
};

DependsOnPill.propTypes = {
  stepKey: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
};

const DependsOnPills = ({ dependsOn, onSelect = null }) => (
  <Stack sx={dependsOnRowStyles}>
    {dependsOn.map(stepKey => (
      <DependsOnPill key={stepKey} stepKey={stepKey} onSelect={onSelect} />
    ))}
  </Stack>
);

DependsOnPills.propTypes = {
  dependsOn: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func,
};

export default memo(DependsOnPills);
